/**
 * BatchImportDialog — read-only multi-record PitchBook comparison.
 *
 * One collapsible row per company with its verdict badge and a side-by-side
 * diff, so an admin can see how each record lines up with the CRM. It never
 * writes and never calls a commit endpoint: PitchBook records reach the CRM
 * only through List Management's reviewed, Admin-confirmed commit (#767), and
 * that source needs PitchBook licensing approval (#777).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
    X, Loader2, AlertTriangle, ChevronDown, ChevronRight,
    Building2, Landmark, User,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
    DiffTable, MovedToListManagement, PersonRow, VerdictBadge,
} from './ImportDiffParts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EntityIcon({ entity, className }) {
    if (entity === 'investor') return <Landmark className={className} />;
    return <Building2 className={className} />;
}

// ─── per-company preview row ──────────────────────────────────────────────────

function CompanyPreviewRow({ block, displayName, expanded, onToggleExpand, expandedPersons, onPersonExpand }) {
    const company = block.company;
    const persons = block.persons || [];
    const isUpdate = company.verdict === 'update' || company.verdict === 'skip_no_change';

    return (
        <div className="border border-stone-200 rounded-xl overflow-hidden">
            {/* Summary bar */}
            <div className="flex items-center gap-3 px-4 py-3 bg-stone-50 border-b border-stone-100">
                <EntityIcon entity={block.entity} className="w-4 h-4 text-stone-400 shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800 text-sm truncate" title={displayName}>
                        {displayName}
                        <span className="ml-2 text-[10px] text-stone-400 font-mono font-normal">{block.pitchbook_id}</span>
                    </p>
                    {company.matched_crm_account && (
                        <p className="text-xs text-stone-400 truncate">→ {company.matched_crm_account.name}</p>
                    )}
                </div>
                <VerdictBadge verdict={company.verdict} />
                {persons.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-stone-500 shrink-0">
                        <User className="w-3 h-3" />
                        {persons.length}
                    </span>
                )}
                <button
                    type="button"
                    onClick={onToggleExpand}
                    className="text-stone-400 hover:text-stone-700 shrink-0"
                    title={expanded ? 'Collapse' : 'Expand diff'}
                >
                    {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            </div>

            {expanded && (
                <div className="p-4 space-y-4">
                    <div>
                        <p className="text-xs font-medium text-stone-500 mb-2">Company fields</p>
                        <DiffTable fields={company.fields} isUpdate={isUpdate} />
                    </div>

                    {persons.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <User className="w-3.5 h-3.5 text-stone-400" />
                                <p className="text-xs font-medium text-stone-500">People ({persons.length})</p>
                            </div>
                            <div className="space-y-2">
                                {persons.map((p) => (
                                    <PersonRow
                                        key={p.pitchbook_id}
                                        person={p}
                                        expanded={!!expandedPersons[p.pitchbook_id]}
                                        onToggleExpand={() => onPersonExpand(p.pitchbook_id)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export default function BatchImportDialog({ pitchbookIds, selectionMeta = {}, onClose }) {
    const { getAuthHeader } = useAuth();

    // ── step: 'loading' | 'review' | 'error'
    const [step, setStep] = useState('loading');
    const [preview, setPreview] = useState(null);     // batch preview response
    const [previewError, setPreviewError] = useState(null);
    const [expanded, setExpanded] = useState({});                 // {[pbId]: bool}
    const [expandedPersons, setExpandedPersons] = useState({});   // {[pbId]: {[personId]: bool}}

    // ── load preview on mount ────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            setStep('loading');
            setPreviewError(null);
            try {
                const res = await axios.post(
                    `${API}/pitchbook/manual-sync/batch-preview`,
                    { pitchbook_ids: pitchbookIds },
                    { headers: getAuthHeader() },
                );
                setPreview(res.data);
                setStep('review');
            } catch (e) {
                const detail = e.response?.data?.detail;
                setPreviewError(typeof detail === 'string' ? detail : 'Failed to load batch preview.');
                setStep('error');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const togglePersonExpand = (pbId, personId) =>
        setExpandedPersons((s) => ({
            ...s, [pbId]: { ...(s[pbId] || {}), [personId]: !(s[pbId] || {})[personId] },
        }));

    const summary = useMemo(() => {
        const blocks = preview?.companies || [];
        return {
            companies: blocks.length,
            persons: blocks.reduce((n, b) => n + (b.persons || []).length, 0),
        };
    }, [preview]);

    // ── render ─────────────────────────────────────────────────────────────────
    return createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
                 data-testid="pb-batch-preview-dialog">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 shrink-0">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {step === 'loading' ? 'Preparing batch preview…' :
                             step === 'review'   ? `Preview ${pitchbookIds.length} record${pitchbookIds.length === 1 ? '' : 's'}` :
                             'Preview error'}
                        </h3>
                        {step === 'review' && (
                            <p className="text-xs text-stone-500 mt-0.5">
                                A read-only comparison with your CRM. Nothing is written from here.
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-700 shrink-0 ml-3" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 min-h-0">
                    {step === 'loading' && (
                        <div className="py-16 text-center text-stone-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <p className="text-xs">Building previews for {pitchbookIds.length} record{pitchbookIds.length === 1 ? '' : 's'}…</p>
                        </div>
                    )}

                    {step === 'error' && (
                        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-800">{previewError}</p>
                        </div>
                    )}

                    {step === 'review' && preview && (
                        <div className="space-y-3">
                            {preview.warnings?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                </div>
                            )}
                            {(preview.companies || []).map((block) => {
                                const pbId = block.pitchbook_id;
                                return (
                                    <CompanyPreviewRow
                                        key={pbId}
                                        block={block}
                                        displayName={block.company?.name || selectionMeta[pbId]?.name || pbId}
                                        expanded={!!expanded[pbId]}
                                        onToggleExpand={() => setExpanded((s) => ({ ...s, [pbId]: !s[pbId] }))}
                                        expandedPersons={expandedPersons[pbId] || {}}
                                        onPersonExpand={(personId) => togglePersonExpand(pbId, personId)}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-stone-200 shrink-0">
                    <div className="flex flex-col gap-1 min-w-0">
                        {step === 'review' && (
                            <span className="text-xs text-stone-500">
                                {summary.companies} compan{summary.companies === 1 ? 'y' : 'ies'} · {summary.persons} person{summary.persons === 1 ? '' : 's'}
                            </span>
                        )}
                        <MovedToListManagement compact />
                    </div>
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                </div>
            </div>
        </div>
    ), document.body);
}
